import { AxiosError } from 'axios';
import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import { authService } from '../../../services/auth';
import { handleRedirectLogin } from '../../../utils/login';
import { generateErrorMessages } from '../../../utils/showApiErrors';
import { PasswordSetupData } from '../components/PasswordSetupForm';
import { SetupState, SetupActions, SetupError, SetupErrorType, SetupStep } from '../types/setup';

export const useSetupFlow = (
  token: string | null,
  redirectUrl: string | null,
  signedInElsewhere = false
  // urlServiceRedirectUrl?: string,
  // urlClientId?: string
): SetupState & SetupActions => {
  const [state, setState] = useState<SetupState>({
    loading: false,
    setupLoading: false,
    userDetails: null,
    mfaLink: null,
    error: null,
    currentStep: 'setup-form',
    loadingState: 'idle'
  });

  const history = useHistory();

  useEffect(() => {
    const loadUserSetupDetails = async () => {
      // A signed-in visitor sees <AlreadyLoggedIn /> instead of the setup form,
      // so don't look the token up yet: the request would go out under the
      // current session's cookies, and a 400/404 from it would paint the
      // "link expired" screen over the log-out prompt. The effect re-runs with
      // the fresh token once they log out.
      if (signedInElsewhere) {
        return;
      }

      if (!token) {
        setState((prev) => ({
          ...prev,
          error: {
            type: SetupErrorType.INVALID_TOKEN,
            message: 'Missing setup token',
            retryable: false,
            statusCode: 404
          }
        }));
        return;
      }

      setState((prev) => ({
        ...prev,
        loading: true,
        loadingState: 'loading-user-details'
      }));

      try {
        const details = await authService.getUserSetupDetails(token);
        setState((prev) => ({
          ...prev,
          userDetails: details,
          loading: false,
          loadingState: 'idle'
        }));
      } catch (error) {
        const axiosError = error as AxiosError<any>;
        const detail = axiosError.response?.data?.detail;

        // Handle token_expired: redirect to register with email pre-filled
        if (
          axiosError.response?.status === 400 &&
          typeof detail === 'object' &&
          detail?.code === 'token_expired' &&
          detail?.email
        ) {
          history.replace(`/register?expired=true&email=${encodeURIComponent(detail.email)}`);
          return;
        }

        const errorMessages = generateErrorMessages(axiosError);
        setState((prev) => ({
          ...prev,
          error: {
            type: SetupErrorType.NETWORK_ERROR,
            message: Array.isArray(errorMessages) ? errorMessages.join('. ') : errorMessages,
            retryable: true,
            statusCode: axiosError.response?.status ?? 404
          },
          loading: false,
          loadingState: 'idle'
        }));
      }
    };

    loadUserSetupDetails();
  }, [token, history, signedInElsewhere]);

  const redirectAfterSetup = async () => {
    // Prefer backend-stored authRequestParams, fall back to URL params from setup link
    // const params = state.userDetails?.authRequestParams;
    // const serviceRedirectUrl = params?.service_redirect_url ?? urlServiceRedirectUrl;
    // const clientId = params?.client_id ?? urlClientId;
    const params = state.userDetails?.authRequestParams;
    const serviceRedirectUrl = params?.service_redirect_url;
    const clientId = params?.client_id;

    if (serviceRedirectUrl) {
      await handleRedirectLogin(serviceRedirectUrl, clientId);
      return;
    }

    if (redirectUrl && redirectUrl !== '/') {
      window.location.href = redirectUrl;
    } else {
      history.push('/');
    }
  };

  const handlePasswordSetup = async (data: PasswordSetupData) => {
    if (!token || !state.userDetails) {
      return;
    }

    setState((prev) => ({
      ...prev,
      setupLoading: true,
      loadingState: 'submitting-password-setup',
      error: null
    }));

    try {
      const response = await authService.setupUser({
        token,
        authType: 'password',
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName
      });

      if (response.mfaRequired) {
        setState((prev) => ({
          ...prev,
          mfaLink: response.mfaLink,
          currentStep: response.mfaLink ? 'mfa-qr' : 'mfa-code',
          setupLoading: false,
          loadingState: 'idle'
        }));
        return;
      }

      setState((prev) => ({
        ...prev,
        currentStep: 'complete',
        setupLoading: false,
        loadingState: 'redirecting'
      }));

      await redirectAfterSetup();
    } catch (error) {
      const axiosError = error as AxiosError;
      const errorMessages = generateErrorMessages(axiosError);
      setState((prev) => ({
        ...prev,
        error: {
          type: SetupErrorType.VALIDATION_ERROR,
          message: Array.isArray(errorMessages) ? errorMessages.join('. ') : errorMessages,
          retryable: true,
          statusCode: axiosError.response?.status ?? 404
        },
        setupLoading: false,
        loadingState: 'idle'
      }));
    }
  };

  const handleSsoSetup = async () => {
    if (!token) {
      return;
    }

    setState((prev) => ({
      ...prev,
      setupLoading: true,
      loadingState: 'submitting-sso-setup',
      error: null
    }));

    try {
      const response = await authService.setupUser({
        token,
        authType: 'sso'
      });

      if (response.mfaRequired) {
        setState((prev) => ({
          ...prev,
          mfaLink: response.mfaLink,
          currentStep: response.mfaLink ? 'mfa-qr' : 'mfa-code',
          setupLoading: false,
          loadingState: 'idle'
        }));
        return;
      }

      setState((prev) => ({
        ...prev,
        currentStep: 'complete',
        setupLoading: false,
        loadingState: 'redirecting'
      }));

      await redirectAfterSetup();
    } catch (error) {
      const axiosError = error as AxiosError;
      const errorMessages = generateErrorMessages(axiosError);
      setState((prev) => ({
        ...prev,
        error: {
          type: SetupErrorType.VALIDATION_ERROR,
          message: Array.isArray(errorMessages) ? errorMessages.join('. ') : errorMessages,
          retryable: true,
          statusCode: axiosError.response?.status ?? 404
        },
        setupLoading: false,
        loadingState: 'idle'
      }));
    }
  };

  const handleMfaVerify = async (code: string) => {
    setState((prev) => ({ ...prev, setupLoading: true, error: null }));
    try {
      await authService.verifyMfa(code);
      setState((prev) => ({ ...prev, currentStep: 'complete', setupLoading: false, loadingState: 'redirecting' }));
      await redirectAfterSetup();
    } catch (error) {
      const axiosError = error as AxiosError;
      const errorMessages = generateErrorMessages(axiosError);
      setState((prev) => ({
        ...prev,
        error: {
          type: SetupErrorType.VALIDATION_ERROR,
          message: Array.isArray(errorMessages) ? errorMessages.join('. ') : errorMessages,
          retryable: true,
          statusCode: axiosError.response?.status ?? 400
        },
        setupLoading: false
      }));
    }
  };

  const setError = (error: SetupError | null) => {
    setState((prev) => ({ ...prev, error }));
  };

  const setCurrentStep = (step: SetupStep) => {
    setState((prev) => ({ ...prev, currentStep: step }));
  };

  const actions: SetupActions = {
    handlePasswordSetup,
    handleSsoSetup,
    handleMfaVerify,
    setError,
    setCurrentStep
  };

  return {
    ...state,
    ...actions
  };
};
